import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import ApiService from "../services/ApiService";
import logo from "../assets/logo.png";
import "./BookingHistory.css";

const BookingHistory = () => {
  const { user: currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("bookings");

  useEffect(() => {
    if (!currentUser) {
      navigate("/auth");
      return;
    }
    fetchData();
  }, [currentUser, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError("");

      const [bookingsResponse, subscriptionsResponse, purchasesResponse] =
        await Promise.all([
          ApiService.getUserBookings().catch(() => ({ data: [] })),
          ApiService.getUserSubscriptions().catch(() => ({ data: [] })),
          ApiService.getUserPurchases().catch(() => ({ data: [] })),
        ]);

      setBookings(bookingsResponse.data || []);
      setSubscriptions(subscriptionsResponse.data || []);
      setPurchases(purchasesResponse.data || []);
    } catch (error) {
      console.error("Error fetching booking history:", error);
      setError("Failed to load booking history. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "confirmed":
      case "completed":
      case "active":
        return "#10b981";
      case "pending":
        return "#f59e0b";
      case "cancelled":
      case "inactive":
        return "#ef4444";
      default:
        return "#6b7280";
    }
  };

  const renderBookings = () => (
    <div className="history-section">
      <h3>Booking History</h3>
      {bookings.length === 0 ? (
        <div className="no-data">
          <p>No bookings found.</p>
          <button
            onClick={() => navigate("/user-dashboard")}
            className="book-now-btn"
          >
            Book Your First Service
          </button>
        </div>
      ) : (
        <div className="history-grid">
          {bookings.map((booking) => (
            <div key={booking.id} className="history-card">
              <div className="card-header">
                <h4>Car Wash Service</h4>
                <span
                  className="status-badge"
                  style={{ backgroundColor: getStatusColor(booking.status) }}
                >
                  {booking.status}
                </span>
              </div>{" "}
              <div className="card-content">
                <div className="detail-row">
                  <span className="label">Date:</span>
                  <span className="value">
                    {formatDate(booking.bookingDate)}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="label">Time Slot:</span>
                  <span className="value">
                    {booking.slot?.startTime && booking.slot?.endTime
                      ? `${booking.slot.startTime} - ${booking.slot.endTime}`
                      : "N/A"}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="label">Car Model:</span>
                  <span className="value">
                    {booking.user?.carModel || "N/A"}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderSubscriptions = () => (
    <div className="history-section">
      <h3>Active Plans</h3>
      {subscriptions.length === 0 ? (
        <div className="no-data">
          <p>No active subscriptions found.</p>
          <button
            onClick={() => navigate("/integrated-purchase-flow")}
            className="book-now-btn"
          >
            Purchase a Plan
          </button>
        </div>
      ) : (
        <div className="history-grid">
          {subscriptions.map((subscription) => (
            <div key={subscription.id} className="history-card">
              <div className="card-header">
                <h4>{subscription.pricingPlan?.name}</h4>
                <span
                  className="status-badge"
                  style={{
                    backgroundColor: getStatusColor(
                      subscription.isActive ? "active" : "inactive"
                    ),
                  }}
                >
                  {subscription.isActive ? "Active" : "Inactive"}
                </span>
              </div>
              <div className="card-content">
                <div className="detail-row">
                  <span className="label">Car Type:</span>
                  <span className="value">{subscription.carType?.name}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Start Date:</span>
                  <span className="value">
                    {formatDate(subscription.startDate)}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="label">End Date:</span>
                  <span className="value">
                    {subscription.endDate
                      ? formatDate(subscription.endDate)
                      : "Unlimited"}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="label">Credits:</span>
                  <span className="value">
                    {subscription.pricingPlan?.creditsGiven}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderPurchases = () => (
    <div className="history-section">
      <h3>Purchase History</h3>
      {purchases.length === 0 ? (
        <div className="no-data">
          <p>No purchases found.</p>
        </div>
      ) : (
        <div className="history-grid">
          {purchases.map((purchase) => (
            <div key={purchase.id} className="history-card">
              {" "}
              <div className="card-header">
                <h4>{purchase.pricingPlan?.name}</h4>
                <span
                  className="status-badge"
                  style={{
                    backgroundColor: getStatusColor(purchase.paymentStatus),
                  }}
                >
                  {purchase.paymentStatus}
                </span>
              </div>{" "}
              <div className="card-content">
                <div className="detail-row">
                  <span className="label">Car Type:</span>
                  <span className="value">{purchase.carType?.name}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Amount:</span>
                  <span className="value">₹{purchase.amount}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Purchase Date:</span>
                  <span className="value">
                    {formatDate(purchase.createdAt)}
                  </span>
                </div>
                {purchase.transactionId && (
                  <div className="detail-row">
                    <span className="label">Transaction ID:</span>
                    <span className="value">{purchase.transactionId}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="booking-history">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-history">
      <header className="dashboard-header">
        <div className="header-content">
          <img src={logo} alt="Steam & Drive" className="dashboard-logo" />
          <div className="user-info">
            <span>
              Welcome, {currentUser?.displayName || currentUser?.email}
            </span>
            <button onClick={handleLogout} className="logout-btn">
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="history-container">
        <div className="history-header">
          <h1>Booking History</h1>
          <button
            onClick={() => navigate("/user-dashboard")}
            className="back-btn"
          >
            ← Back to Dashboard
          </button>
        </div>

        {error && (
          <div className="error-message">
            <p>{error}</p>
          </div>
        )}

        <div className="history-tabs">
          <button
            className={`tab-btn ${activeTab === "bookings" ? "active" : ""}`}
            onClick={() => setActiveTab("bookings")}
          >
            Bookings ({bookings.length})
          </button>
          <button
            className={`tab-btn ${
              activeTab === "subscriptions" ? "active" : ""
            }`}
            onClick={() => setActiveTab("subscriptions")}
          >
            Active Plans ({subscriptions.length})
          </button>
          <button
            className={`tab-btn ${activeTab === "purchases" ? "active" : ""}`}
            onClick={() => setActiveTab("purchases")}
          >
            Purchases ({purchases.length})
          </button>
        </div>

        <div className="history-content">
          {activeTab === "bookings" && renderBookings()}
          {activeTab === "subscriptions" && renderSubscriptions()}
          {activeTab === "purchases" && renderPurchases()}
        </div>
      </div>
    </div>
  );
};

export default BookingHistory;
